import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Markdown from "react-markdown";
import ImageRenderer from "../util/ImageRenderer";
import missions from "../content/missions.json";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import Divider from "@mui/material/Divider";
import NotFound from "./NotFound";
import { useTheme } from "@mui/material/styles";

const Mission = () => {
  const theme = useTheme();
  const { id } = useParams();
  const mission = missions.find((item) => item.destination === `/missions/${id}`);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setReport(null);
    setError(null);
    if (!mission) return;
    const controller = new AbortController();
    fetch(`/content/reports/${id}.md`, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error("Report unavailable");
        const text = await response.text();
        if (text.trimStart().startsWith("<!DOCTYPE")) throw new Error("Report unavailable");
        if (!controller.signal.aborted) setReport({ id, content: text });
      })
      .catch((reason) => {
        if (!controller.signal.aborted) setError(id);
      });
    return () => controller.abort();
  }, [id, mission]);

  if (mission == null) return <NotFound />;

  return (
    <Box sx={{ bgcolor: "background.default" }}>
      <Box
        sx={{
          px: { xs: 2, sm: 4 },
          py: { xs: 7, md: 9 },
          background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
          color: theme.palette.common.white,
          textAlign: "center",
        }}
      >
        <Chip
          label="Mission report"
          sx={{
            bgcolor: "rgba(255,255,255,0.1)",
            color: theme.palette.common.white,
            border: "1px solid rgba(255,255,255,0.2)",
            mb: 2,
            fontWeight: 800,
          }}
        />
        <Typography
          variant="h1"
          sx={{
            fontFamily: "Georgia, 'Times New Roman', serif",
            fontStyle: "italic",
            fontWeight: 500,
            fontSize: { xs: "2.4rem", md: "4rem" },
            lineHeight: 1.08,
          }}
        >
          {mission.title} · {mission.date}
        </Typography>
      </Box>

      <Box sx={{ maxWidth: 980, mx: "auto", px: { xs: 2, sm: 4 }, py: 6 }}>
        <Box
          sx={{
            bgcolor: "white",
            borderRadius: 2,
            border: "1px solid rgba(6, 57, 112, 0.1)",
            boxShadow: "0 18px 48px rgba(6, 57, 112, 0.1)",
            px: { xs: 2.25, md: 5 },
            py: { xs: 3, md: 5 },
            "& p": {
              color: "text.secondary",
              fontSize: { xs: "1rem", md: "1.06rem" },
              lineHeight: 1.85,
            },
            "& ul, & ol": {
              color: "text.secondary",
              lineHeight: 1.8,
              pl: 3,
            },
            "& li": {
              mb: 0.75,
            },
            "& strong": {
              color: theme.palette.primary.main,
            },
          }}
        >
          {error === id ? (
            <Typography role="alert">The report could not be loaded. Please refresh to try again, or <Link href="/missions">return to missions</Link>.</Typography>
          ) : !report || report.id !== id ? (
            <Typography role="status">Loading mission report…</Typography>
          ) : <Markdown
            components={{
              img: ImageRenderer,
              h1: ({ node, ...props }) => (
                <Typography
                  variant="h2"
                  sx={{
                    color: theme.palette.primary.main,
                    fontFamily: "Georgia, 'Times New Roman', serif",
                    fontStyle: "italic",
                    fontWeight: 500,
                    fontSize: { xs: "2rem", md: "3rem" },
                    mb: 2,
                  }}
                  {...props}
                />
              ),
              h2: ({ node, ...props }) => (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography
                    variant="h4"
                    component="h3"
                    sx={{
                      color: theme.palette.primary.main,
                      fontFamily: "Georgia, 'Times New Roman', serif",
                      fontStyle: "italic",
                      fontWeight: 500,
                      mb: 1.5,
                    }}
                    {...props}
                  />
                </>
              ),
              h3: ({ node, ...props }) => (
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{ color: theme.palette.primary.main, fontWeight: 900, mt: 2 }}
                  {...props}
                />
              ),
              a: ({ node, ...props }) => (
                <Link
                  {...props}
                  target={props.href?.startsWith("https://") ? "_blank" : undefined}
                  rel={props.href?.startsWith("https://") ? "noopener noreferrer" : undefined}
                  sx={{ color: theme.palette.primary.main, fontWeight: 800 }}
                />
              ),
            }}
          >
            {report.content}
          </Markdown>}
        </Box>
      </Box>
    </Box>
  );
};

export default Mission;
